import React, { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot, addDoc } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

export default function EmployeeHandbookPage() {
  const { orgId, isAdmin } = useAuth()
  const [manuals, setManuals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  // Seed demo docs on first load
  useEffect(() => {
    if (!orgId) return
    ;(async () => {
      const { getDocs } = await import('firebase/firestore')
      const snap = await getDocs(query(collection(db, 'organizations', orgId, 'employeeHandbook'), where('orgId', '==', orgId)))
      if (snap.empty) {
        const seedDocs = [
          { title: 'Company Safety Policy Handbook', fileUrl: '/pdfs/osha_fall_protection.pdf', fileType: 'pdf', orgId, acknowledgments: ['John Smith'], createdAt: new Date(Date.now() - 5 * 86400000).toISOString(), updatedAt: new Date().toISOString() },
          { title: 'Employee Code of Conduct & Procedures', fileUrl: '/pdfs/osha_scaffolding.pdf', fileType: 'pdf', orgId, acknowledgments: [], createdAt: new Date(Date.now() - 2 * 86400000).toISOString(), updatedAt: new Date().toISOString() },
        ]
        for (const doc of seedDocs) await addDoc(collection(db, 'organizations', orgId, 'employeeHandbook'), doc)
      }
    })()
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'employeeHandbook'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')),
      snap => { setManuals(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) }
    )
    return () => unsub()
  }, [orgId])

  const handleUpload = async () => {
    const title = prompt('Document title:')
    if (!title) return
    await addDoc(collection(db, 'organizations', orgId, 'employeeHandbook'), {
      orgId, title, fileUrl: '', fileType: 'pdf',
      acknowledgments: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
    })
  }

  const formatDate = (iso: string) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

  return (
    <AppLayout title="Employee Handbook">
      <div className="mb-6 flex items-center justify-between">
        <input type="text" placeholder="Search documents..." className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64" />
        {isAdmin() && (
          <button onClick={handleUpload} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
            + Upload Document
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-6">
        {manuals.map(m => {
          const ackCount = m.acknowledgments?.length || 0
          return (
            <div key={m.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-start gap-3 mb-4">
                <span className="text-3xl">📘</span>
                <div>
                  <h3 className="font-semibold text-gray-900">{m.title}</h3>
                  <p className="text-xs text-gray-400 mt-1">Uploaded {formatDate(m.createdAt)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{ackCount} acknowledgments</span>
                {m.fileUrl && (
                  <a href={m.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 text-sm hover:underline">Open PDF</a>
                )}
              </div>
            </div>
          )
        })}
        {manuals.length === 0 && (
          <p className="col-span-3 text-center py-12 text-gray-400">No employee handbook documents found</p>
        )}
      </div>
    </AppLayout>
  )
}
